import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@openedx/paragon';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import {
  BookOpen, CreditCard, Description, InsertChartOutlined, MoneyOutline,
  Person, ReceiptLong, Settings, Support, Tag, TrendingUp,
} from '@openedx/paragon/icons';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform/config';
import { logError } from '@edx/frontend-platform/logging';

import IconLink from './IconLink';
import { configuration, features } from '../../config';
import { SubsidyRequestsContext } from '../subsidy-requests';
import { ROUTE_NAMES, PRODUCT_TYPES } from '../EnterpriseApp/data/constants';
import { TOUR_TARGETS } from '../ProductTours/constants';
import {
  ANALYTICS_INSIGHTS_TARGETS,
  ADMINISTER_SUBSCRIPTIONS_TARGETS,
  CUSTOMIZE_REPORTS_SIDEBAR,
  TRACK_LEARNER_PROGRESS_TARGETS,
} from '../ProductTours/AdminOnboardingTours/constants';
import { useOnMount } from '../../hooks';
import { EnterpriseSubsidiesContext } from '../EnterpriseSubsidiesContext';
import { EnterpriseAppContext } from '../EnterpriseApp/EnterpriseAppContextProvider';
import LmsApiService from '../../data/services/LmsApiService';
import { GROUP_TYPE_BUDGET } from '../PeopleManagement/constants';
import { isBillingEnabled } from '../billing/data/utils';

const Sidebar = ({
  baseUrl,
  expandSidebar,
  collapseSidebar,
  isExpanded,
  isExpandedByToggle,
  enableCodeManagementScreen,
  enableReportingConfigScreen,
  enableSubscriptionManagementScreen,
  enableAnalyticsScreen,
  enablePeopleManagementScreen,
  onWidthChange,
  isMobile,
  onMount,
}) => {
  const sidebarRef = useRef();
  const sidebarContentRef = useRef();
  const sidebarNavRef = useRef();
  const sidebarWidthRef = useRef();
  const {
    enterpriseCuration: { enterpriseCuration, isNewArchivedContent },
  } = useContext(EnterpriseAppContext);
  const { subsidyRequestsCounts } = useContext(SubsidyRequestsContext);
  const { canManageLearnerCredit, hasBillingSubscription, productType } = useContext(EnterpriseSubsidiesContext);
  const { FEATURE_CONTENT_HIGHLIGHTS } = getConfig();
  const authenticatedUser = getAuthenticatedUser();
  const isEdxStaff = authenticatedUser.administrator;
  const [hasBudgetGroup, setHasBudgetGroup] = useState(false);
  const hideHighlightsForGroups = hasBudgetGroup && !isEdxStaff;
  const intl = useIntl();
  const isEssentials = productType === PRODUCT_TYPES.ESSENTIALS;

  // Determine if billing features should be accessible
  // Note: All users of the enterprise admin portal are already authenticated as enterprise admins
  const canAccessBilling = isBillingEnabled(hasBillingSubscription);

  const getSidebarWidth = useCallback(() => {
    if (sidebarRef && sidebarRef.current) {
      const { width } = sidebarRef.current.getBoundingClientRect();
      return width;
    }
    return null;
  }, []);

  useOnMount(() => {
    if (!sidebarContentRef.current || !sidebarNavRef.current) {
      return;
    }
    const sidebarContentPadding = {
      top: parseInt(global.getComputedStyle(sidebarContentRef.current).paddingTop, 10),
      bottom: parseInt(global.getComputedStyle(sidebarContentRef.current).paddingBottom, 10),
    };
    const sidebarNavHeight = sidebarNavRef.current.getBoundingClientRect().height;
    const sidebarHeight = sidebarNavHeight + sidebarContentPadding.top + sidebarContentPadding.bottom;
    onMount({ sidebarHeight });
  });

  useOnMount(() => {
    if (isExpandedByToggle) {
      // If sidebar is already expanded via the toggle on mount
      const sideBarWidth = getSidebarWidth();
      sidebarWidthRef.current = sideBarWidth;
      onWidthChange(sideBarWidth);
    }
  });

  useOnMount(() => {
    async function fetchGroupsData() {
      try {
        const response = await LmsApiService.fetchEnterpriseGroups();
        // we only want to hide the feature if a customer has a group this does not
        // apply to all contexts/include all users
        response.data.results.forEach((group) => {
          if (group.group_type === GROUP_TYPE_BUDGET) {
            setHasBudgetGroup(true);
          }
        });
      } catch (error) {
        logError(error);
      }
    }
    if (!isEdxStaff) {
      fetchGroupsData();
    }
  });

  useEffect(() => {
    const sideBarWidth = getSidebarWidth();
    if (sidebarWidthRef.current !== sideBarWidth) {
      if (!isExpanded) {
        onWidthChange(sideBarWidth);
      }
      sidebarWidthRef.current = sideBarWidth;
    }
  }, [getSidebarWidth, isExpanded, isExpandedByToggle, isMobile, onWidthChange]);

  const getMenuItems = () => [
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.learner.progress.report.title',
        defaultMessage: 'Learner Progress Report',
        description: 'Sidebar menu item title for learner progress report.',
      }),
      to: `${baseUrl}/admin/${ROUTE_NAMES.learners}`,
      icon: <Icon src={TrendingUp} />,
      id: TRACK_LEARNER_PROGRESS_TARGETS.LEARNER_PROGRESS_SIDEBAR,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.analytics.title',
        defaultMessage: 'Analytics',
        description: 'Sidebar menu item title for analytics.',
      }),
      to: `${baseUrl}/admin/${ROUTE_NAMES.analytics}`,
      icon: <Icon src={InsertChartOutlined} />,
      hidden: !features.ANALYTICS || !enableAnalyticsScreen || isEssentials,
      id: ANALYTICS_INSIGHTS_TARGETS.SIDEBAR,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.code.management.title',
        defaultMessage: 'Code Management',
        description: 'Sidebar menu item title for code management.',
      }),
      to: `${baseUrl}/admin/${ROUTE_NAMES.codeManagement}`,
      icon: <Icon src={Tag} />,
      hidden: !features.CODE_MANAGEMENT || !enableCodeManagementScreen,
      notification: !!subsidyRequestsCounts.couponCodes,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.subscription.management.title',
        defaultMessage: 'Subscription Management',
        description: 'Sidebar menu item title for subscription management.',
      }),
      to: `${baseUrl}/admin/${ROUTE_NAMES.subscriptionManagement}`,
      icon: <Icon src={CreditCard} />,
      hidden: !enableSubscriptionManagementScreen,
      notification: !!subsidyRequestsCounts.subscriptionLicenses,
      id: ADMINISTER_SUBSCRIPTIONS_TARGETS.SIDEBAR,
    },
    {
      title: <FormattedMessage
        id="admin.portal.billing.nav.label"
        defaultMessage="Billing"
      />,
      to: `${baseUrl}/admin/${ROUTE_NAMES.billing}`,
      icon: <Icon src={ReceiptLong} />,
      hidden: !canAccessBilling,
      id: TOUR_TARGETS.BILLING,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.learner.credit.management.title',
        defaultMessage: 'Learner Credit Management',
        description: 'Sidebar menu item title for learner credit management.',
      }),
      id: TOUR_TARGETS.LEARNER_CREDIT,
      to: `${baseUrl}/admin/${ROUTE_NAMES.learnerCredit}`,
      icon: <Icon src={MoneyOutline} />,
      hidden: !canManageLearnerCredit,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.people.management.title',
        defaultMessage: 'People Management',
        description: 'Sidebar menu item title for people management.',
      }),
      id: TOUR_TARGETS.PEOPLE_MANAGEMENT,
      to: `${baseUrl}/admin/${ROUTE_NAMES.peopleManagement}`,
      icon: <Icon src={Person} />,
      hidden: !enablePeopleManagementScreen,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.highlights.title',
        defaultMessage: 'Highlights',
        description: 'Sidebar menu item title for highlights.',
      }),
      id: TOUR_TARGETS.CONTENT_HIGHLIGHTS,
      to: `${baseUrl}/admin/${ROUTE_NAMES.contentHighlights}`,
      icon: <Icon src={BookOpen} />,
      hidden: !FEATURE_CONTENT_HIGHLIGHTS || !enterpriseCuration?.isHighlightFeatureActive || hideHighlightsForGroups,
      notification: isNewArchivedContent,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.reporting.configurations.title',
        defaultMessage: 'Reporting Configurations',
        description: 'Sidebar menu item title for reporting configurations.',
      }),
      to: `${baseUrl}/admin/${ROUTE_NAMES.reporting}`,
      icon: <Icon src={Description} />,
      hidden: !features.REPORTING_CONFIGURATIONS || !enableReportingConfigScreen,
      id: CUSTOMIZE_REPORTS_SIDEBAR,
    },
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.settings.title',
        defaultMessage: 'Settings',
        description: 'Sidebar menu item title for settings.',
      }),
      id: TOUR_TARGETS.SETTINGS_SIDEBAR,
      to: `${baseUrl}/admin/${ROUTE_NAMES.settings}`,
      icon: <Icon src={Settings} />,
    },
    // NOTE: keep "Support" link the last nav item
    {
      title: intl.formatMessage({
        id: 'sidebar.menu.item.support.title',
        defaultMessage: 'Support',
        description: 'Sidebar menu item title for support.',
      }),
      to: configuration.ENTERPRISE_SUPPORT_URL,
      icon: <Icon src={Support} />,
      hidden: !features.SUPPORT,
      external: true,
    },
  ];

  const isSidebarExpanded = isExpanded || isExpandedByToggle;
  // Only collapse sidebar if it's already expanded and wasn't expanded by the toggle
  const shouldSidebarCollapse = isSidebarExpanded && !isExpandedByToggle;
  const hasMobileShadow = isMobile && isSidebarExpanded;

  return (
    <nav
      id="sidebar"
      aria-label="sidebar"
      data-testid="nav-sidebar"
      className={classNames([
        'sidebar',
        'border-right',
        'h-100',
        'd-none',
        'd-lg-flex',
        {
          'd-flex': isSidebarExpanded,
          expanded: isSidebarExpanded,
          'has-shadow': !isExpandedByToggle || hasMobileShadow,
        },
      ])}
      onMouseOver={() => !isSidebarExpanded && expandSidebar()}
      onFocus={() => !isSidebarExpanded && expandSidebar()}
      onMouseLeave={() => shouldSidebarCollapse && collapseSidebar()}
      onBlur={() => shouldSidebarCollapse && collapseSidebar()}
      ref={sidebarRef}
    >
      <div className="sidebar-content py-2" ref={sidebarContentRef}>
        <ul className="nav nav-pills flex-column m-0" ref={sidebarNavRef}>
          {getMenuItems().filter(item => !item.hidden).map(({
            id, to, title, icon, notification, external,
          }) => (
            <li key={to} className="nav-item">
              <IconLink
                id={id}
                to={to}
                title={title}
                icon={icon}
                notification={notification}
                external={external}
                isExpanded={isSidebarExpanded}
                onClick={collapseSidebar}
              />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

Sidebar.defaultProps = {
  enableCodeManagementScreen: false,
  enableReportingConfigScreen: false,
  enableSubscriptionManagementScreen: false,
  enableAnalyticsScreen: false,
  enablePeopleManagementScreen: true,
  onWidthChange: () => {},
  isMobile: false,
};

Sidebar.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  expandSidebar: PropTypes.func.isRequired,
  collapseSidebar: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isExpandedByToggle: PropTypes.bool.isRequired,
  enableCodeManagementScreen: PropTypes.bool,
  enableReportingConfigScreen: PropTypes.bool,
  enableSubscriptionManagementScreen: PropTypes.bool,
  enableAnalyticsScreen: PropTypes.bool,
  enablePeopleManagementScreen: PropTypes.bool,
  onWidthChange: PropTypes.func,
  onMount: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

export default Sidebar;
